import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, BookOpen, TrendingUp, ArrowRight, ArrowLeft, Users, Building2, ExternalLink, Calendar, Database } from 'lucide-react';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import analytics from '@/utils/analytics';

const API = window.location.origin + '/api';

const ResourceArticle = () => {
  const { slug } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/resources/${slug}`);
        setContent(response.data);
        
        // Track resource page view with details
        const data = response.data;
        analytics.resourcePageView(slug, data.type, data.niche || null, data.region || null);
      } catch (err) {
        console.error('Failed to load resource:', err);
        setError('Resource not found');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen bg-slate-50">
        <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-indigo-600" />
              <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
            </Link>
          </div>
        </nav>
        <div className="max-w-4xl mx-auto px-6 py-24 text-center">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Resource Not Found</h1>
          <p className="text-slate-600 mb-8">The resource you're looking for doesn't exist.</p>
          <Link to="/resources">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resources
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const { title, meta_description, h1, sections, brands, brand_count, total_mentions } = content;
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title={title}
        description={meta_description}
        path={`/resources/${slug}`}
        type="article"
      />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="home-logo-link">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>Sponsiq</span>
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/resources">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Resources
              </Button>
            </Link>
            <Link to="/explore-brands">
              <Button variant="ghost" size="sm">Discover Brands</Button>
            </Link>
            <Link to="/brands/discover">
              <Button variant="ghost" size="sm">Discover Creators</Button>
            </Link>
            <Link to="/">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm">
                Analyze Channel
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <header className="bg-gradient-to-b from-indigo-50 to-slate-50 py-12">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/resources" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Resources
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}} data-testid="article-h1">
            {h1}
          </h1>
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Updated {currentYear}
            </span>
            {brand_count > 0 && (
              <span className="flex items-center gap-1">
                <Building2 className="w-4 h-4" />
                {brand_count} brands tracked
              </span>
            )}
            {total_mentions > 0 && (
              <span className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                {total_mentions.toLocaleString()} mentions
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Introduction */}
          {sections?.introduction && (
            <section className="mb-10">
              <div className="prose prose-slate max-w-none">
                {sections.introduction.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-slate-600 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          )}

          {/* Top Brands List */}
          {brands && brands.length > 0 && (
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2" style={{fontFamily: 'Manrope'}}>
                <Building2 className="w-6 h-6 text-indigo-600" />
                Top Sponsoring Brands
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {brands.slice(0, 10).map((brand, index) => (
                  <Card key={brand.brand_name || index} className="p-4 hover:shadow-md transition-shadow" data-testid={`brand-item-${index}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>
                            {index + 1}. {brand.brand_name}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          {brand.niche_category || 'General'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-indigo-600" style={{fontFamily: 'JetBrains Mono'}}>
                          {brand.mention_count || 0}
                        </p>
                        <p className="text-xs text-slate-500">mentions</p>
                      </div>
                    </div>
                    {brand.sample_videos && brand.sample_videos.length > 0 && (
                      <p className="text-xs text-slate-500 mt-2 line-clamp-1">
                        Featured in: {brand.sample_videos[0]}
                      </p>
                    )}
                  </Card>
                ))}
              </div>
              {brands.length > 10 && (
                <p className="text-sm text-slate-500 mt-4 text-center">
                  + {brands.length - 10} more brands tracked
                </p>
              )}
            </section>
          )}

          {/* Insights */}
          {sections?.insights && (
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                Key Insights & Trends
              </h2>
              <Card className="p-6 bg-indigo-50 border-indigo-200">
                <div className="prose prose-slate max-w-none">
                  {sections.insights.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-slate-700 leading-relaxed mb-3 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </Card>
            </section>
          )}

          {/* Conclusion */}
          {sections?.conclusion && (
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Manrope'}}>
                Next Steps
              </h2>
              <div className="prose prose-slate max-w-none">
                {sections.conclusion.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-slate-600 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          )}

          {/* CTA Section - Strong Product Focus */}
          <section className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-3" style={{fontFamily: 'Manrope'}}>
              Explore Real Sponsor Data
            </h3>
            <p className="text-indigo-100 mb-6">
              See the actual brands sponsoring YouTube creators. Find creators working with these brands and discover partnership opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/explore-brands">
                <Button className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-6 h-11">
                  <Building2 className="w-4 h-4 mr-2" />
                  Explore Sponsoring Brands
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/brands/discover">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 px-6 h-11 font-semibold">
                  <Users className="w-4 h-4 mr-2" />
                  Find Creators with Sponsors
                </Button>
              </Link>
            </div>
          </section>
        </div>
      </article>

      {/* Related Links */}
      <section className="bg-white border-t border-slate-200 py-12">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Manrope'}}>
            Explore More
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/brands/discover" className="group">
              <Card className="p-4 hover:shadow-md transition-shadow h-full">
                <Users className="w-8 h-8 text-indigo-600 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-600">
                  Discover Creators
                </h3>
                <p className="text-sm text-slate-600">
                  Find YouTube creators perfect for brand sponsorships
                </p>
              </Card>
            </Link>
            <Link to="/explore-brands" className="group">
              <Card className="p-4 hover:shadow-md transition-shadow h-full">
                <Building2 className="w-8 h-8 text-emerald-600 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-emerald-600">
                  Browse Brands
                </h3>
                <p className="text-sm text-slate-600">
                  Explore brands actively sponsoring creators
                </p>
              </Card>
            </Link>
            <Link to="/leaderboards" className="group">
              <Card className="p-4 hover:shadow-md transition-shadow h-full">
                <TrendingUp className="w-8 h-8 text-amber-600 mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-amber-600">
                  Leaderboards
                </h3>
                <p className="text-sm text-slate-600">
                  Top creators ranked by audience size
                </p>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      <Footer showDisclaimer={true} />
    </div>
  );
};

export default ResourceArticle;
